/**
 * État du dossier — chunk 9.AC.1.
 *
 * Répond aux cinq questions que le conseiller se pose devant un projet :
 * **où j'en suis, ce qui manque, qui doit fournir, quelles échéances approchent,
 * quelle est la prochaine action.** Aucune n'avait de réponse dans le produit
 * avant ce chunk.
 *
 * ## Ce composant ne décide rien
 *
 * Statut, compteurs, `waiting_on`, `next_action`, retards : tout arrive calculé du
 * serveur. Décision PO explicite — « le frontend ne doit jamais reconstruire cette
 * logique ». La conséquence pratique compte autant que le principe : le jour où la
 * règle « une chose m'incombe → c'est à moi de jouer » évoluera, elle évoluera à un
 * seul endroit, et l'interface suivra sans qu'on y touche.
 *
 * ## Le CTA propose, il n'agit pas
 *
 * Quand le dossier est prêt, on propose la préparation des demandes assureurs. On
 * ne la crée pas, on n'envoie rien : décision PO explicite. Un envoi automatique
 * sur un dossier « prêt » serait irréversible, et « prêt » est un verdict
 * mécanique — le conseiller reste seul juge du moment.
 */

import type {
  ClientCommunication,
  CompletenessStatus,
  NextAction,
  ProjectCompleteness,
  RequirementState,
} from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  useProjectCompleteness,
  useReconcileCompleteness,
} from "../../../lib/queries/projectCompleteness";

import { useClientCommunications } from "../../../lib/queries/clientCommunications";

import { AddRequirementDialog } from "./AddRequirementDialog";
import { AskClientDialog } from "./AskClientDialog";

type Props = {
  readonly projectId: string;
  /** Entreprise du dossier — restreint les destinataires possibles (9.AD.1). */
  readonly companyId?: string | null;
};

/**
 * Rend une charge de verdict exploitable, quoi qu'il arrive.
 *
 * Le typage promet `waiting_on` et `requirements` ; la réalité ne le garantit
 * pas — réponse en cache d'avant ce chunk, serveur non redéployé, proxy qui
 * réécrit, ou simplement un test dont le mock ne connaît pas encore la route.
 * Sans cette garde, `v.waiting_on.length` lève et **emporte toute la page
 * projet** : le conseiller perd l'accès aux soumissions, aux demandes assureurs
 * et aux pièces à cause d'un encart d'état.
 *
 * C'est exactement le défaut qui avait fait tomber l'écran de soumission au chunk
 * 9.AA.1b (`PlanPreview` sur un plan sans `selected_passes`). Les sentinelles de
 * `ProjectDetailPage` l'ont attrapé ici aussi — deuxième fois qu'elles paient.
 */
function normaliserVerdict(brut: ProjectCompleteness | undefined): ProjectCompleteness | null {
  if (!brut || typeof brut !== "object") return null;
  const statuts: CompletenessStatus[] = ["ready", "action_required", "waiting_on_others"];
  if (!statuts.includes(brut.completeness_status)) return null;
  return {
    ...brut,
    waiting_on: Array.isArray(brut.waiting_on) ? brut.waiting_on : [],
    requirements: Array.isArray(brut.requirements) ? brut.requirements : [],
    requirements_total: Number.isFinite(brut.requirements_total) ? brut.requirements_total : 0,
    requirements_satisfied: Number.isFinite(brut.requirements_satisfied)
      ? brut.requirements_satisfied
      : 0,
    requirements_missing: Number.isFinite(brut.requirements_missing)
      ? brut.requirements_missing
      : 0,
    overdue_count: Number.isFinite(brut.overdue_count) ? brut.overdue_count : 0,
    due_soon_count: Number.isFinite(brut.due_soon_count) ? brut.due_soon_count : 0,
  };
}

/**
 * Trois états, trois traitements visuels distincts.
 *
 * `action_required` est ambre et non rouge : il ne s'agit pas d'une erreur mais
 * d'un travail à faire. Le rouge est réservé aux retards, pour qu'il garde son
 * pouvoir d'alerte — un écran où tout est rouge ne signale plus rien.
 */
const TON: Record<CompletenessStatus, string> = {
  ready: "border-emerald-200 bg-emerald-50 text-emerald-900",
  action_required: "border-amber-200 bg-amber-50 text-amber-900",
  waiting_on_others: "border-sky-200 bg-sky-50 text-sky-900",
};

function LigneRequis({
  etat,
  demandes,
  onDemander,
}: {
  readonly etat: RequirementState;
  /** Communications déjà envoyées pour CE requis. */
  readonly demandes: readonly ClientCommunication[];
  readonly onDemander: (etat: RequirementState) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("en") ? "en" : "fr";
  const r = etat.requirement;
  // Même prudence au niveau de la ligne : une entrée sans `requirement` ne doit
  // pas faire tomber la liste entière.
  if (!r) return null;
  const libelle = (lang === "en" ? r.label_en : r.label_fr) ?? "";
  // La plus récente : la liste arrive déjà triée du serveur (created_at DESC).
  const derniere = demandes[0];
  // On ne propose de demander QUE ce qui incombe au client et n'est pas satisfait.
  // Proposer « demander au client » sur un requis qui nous incombe reviendrait à
  // lui réclamer un document interne.
  const demandable = !etat.satisfied && r.audience === "client";

  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1 border-t border-slate-100 py-3 first:border-t-0">
      <span
        aria-hidden="true"
        className={`mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full ${
          etat.satisfied ? "bg-emerald-500" : etat.overdue ? "bg-red-500" : "bg-amber-500"
        }`}
      />
      <span className="min-w-0 flex-1">
        <span className={etat.satisfied ? "text-slate-500 line-through" : "text-slate-900"}>
          {libelle}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
          <span>{t(`completeness.kind.${r.kind}`)}</span>
          {!etat.satisfied && (
            <>
              <span aria-hidden="true">·</span>
              <span>{t(`completeness.waiting_on.${r.audience}`)}</span>
            </>
          )}
          {etat.reason === "waived" && (
            <>
              <span aria-hidden="true">·</span>
              <span>{t("completeness.reason.waived")}</span>
            </>
          )}
          {/* 9.AD.1 — « a-t-on déjà demandé ? », répondu sur la ligne elle-même.
              Le conseiller ne devrait pas avoir à descendre dans une trace pour
              savoir s'il a déjà écrit : c'est la question qu'il se pose en
              regardant le requis. */}
          {derniere !== undefined && (
            <>
              <span aria-hidden="true">·</span>
              <span title={derniere.to_email}>
                {t("client_comms.asked_on", {
                  date: new Date(derniere.created_at).toLocaleDateString(lang),
                  name: derniere.contact_name ?? "",
                })}
                {derniere.delivery_status !== null
                  ? ` · ${t(`client_comms.delivery.${derniere.delivery_status}`)}`
                  : derniere.status === "queued"
                    ? ` · ${t("client_comms.queued")}`
                    : ""}
              </span>
            </>
          )}
          {r.due_at !== null && (
            <>
              <span aria-hidden="true">·</span>
              <span className={etat.overdue ? "font-medium text-red-700" : undefined}>
                {etat.overdue
                  ? t("completeness.due.overdue", {
                      date: new Date(r.due_at).toLocaleDateString(lang),
                    })
                  : t("completeness.due.on", {
                      date: new Date(r.due_at).toLocaleDateString(lang),
                    })}
              </span>
            </>
          )}
        </span>
      </span>
      {demandable && (
        <button
          type="button"
          onClick={() => onDemander(etat)}
          className="focus-visible:ring-brand-500 shrink-0 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
        >
          {derniere !== undefined ? t("client_comms.ask_again") : t("client_comms.ask")}
        </button>
      )}
    </li>
  );
}

export function DossierReadinessSection({ projectId, companyId = null }: Props) {
  const { t } = useTranslation();
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [demande, setDemande] = useState<RequirementState | null>(null);
  const verdict = useProjectCompleteness(projectId);
  const reconcilier = useReconcileCompleteness(projectId);
  const communications = useClientCommunications(projectId);

  // Index des demandes par requis. Un envoi de formulaire se rattache au lien, une
  // demande d'information au manquant — deux clés, une seule question posée par le
  // conseiller : « ai-je déjà écrit à propos de ça ? »
  const parRequis = new Map<string, ClientCommunication[]>();
  for (const c of communications.data?.items ?? []) {
    const cle = c.missing_information_id ?? c.intake_link_id ?? "";
    if (cle === "") continue;
    const liste = parRequis.get(cle) ?? [];
    liste.push(c);
    parRequis.set(cle, liste);
  }

  if (verdict.isPending) {
    return (
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-slate-500" aria-live="polite">
          {t("completeness.loading")}
        </p>
      </section>
    );
  }

  const v = normaliserVerdict(verdict.data);

  if (verdict.isError || v === null) {
    return (
      <section className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm">
        <p className="text-sm text-red-800" role="alert">
          {t("completeness.error")}
        </p>
      </section>
    );
  }

  const action: NextAction = v.next_action;

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900">{t("completeness.title")}</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setAjoutOuvert(true)}
            className="focus-visible:ring-brand-500 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("completeness.add_requirement")}
          </button>
          <button
            type="button"
            onClick={() => reconcilier.mutate()}
            disabled={reconcilier.isPending}
            className="focus-visible:ring-brand-500 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
          >
            {reconcilier.isPending ? t("completeness.rechecking") : t("completeness.recheck")}
          </button>
        </div>
      </div>

      {/* Le verdict, en une phrase que le conseiller lit sans interpréter. */}
      <div
        className={`mt-4 rounded-lg border px-4 py-3 ${action === "define_requirements" ? "border-slate-200 bg-slate-50 text-slate-800" : TON[v.completeness_status]}`}
      >
        <p className="text-sm font-semibold">
          {t(
            action === "define_requirements"
              ? "completeness.undefined"
              : `completeness.status.${v.completeness_status}`,
          )}
        </p>
        <p className="mt-1 text-sm">{t("completeness.scope")}</p>
        <p className="mt-1 text-sm">
          {v.requirements_total === 0
            ? t("completeness.summary.none")
            : t("completeness.summary.counts", {
                satisfied: v.requirements_satisfied,
                total: v.requirements_total,
              })}
        </p>

        {v.waiting_on.length > 0 && (
          <p className="mt-1 text-sm">
            {t("completeness.waiting_prefix")}{" "}
            {v.waiting_on.map((a) => t(`completeness.waiting_on.${a}`)).join(", ")}
          </p>
        )}

        {(v.overdue_count > 0 || v.due_soon_count > 0) && (
          <p className="mt-1 text-sm">
            {v.overdue_count > 0 && (
              <span className="font-medium">
                {t("completeness.overdue", { count: v.overdue_count })}
              </span>
            )}
            {v.overdue_count > 0 && v.due_soon_count > 0 && " · "}
            {v.due_soon_count > 0 && t("completeness.due_soon", { count: v.due_soon_count })}
          </p>
        )}

        {/* La prochaine action — toujours présente, jamais devinée par l'UI. */}
        <p className="mt-3 text-sm font-medium">{t(`completeness.next_action.${action}`)}</p>

        {action === "prepare_quote_requests" && (
          <Link
            to={`/projects/${projectId}#quote-requests`}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 mt-2 inline-block rounded-lg px-3 py-1.5 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("completeness.cta_quote_requests")}
          </Link>
        )}
      </div>

      {v.requirements.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-600">
          {t("completeness.empty")}
        </p>
      ) : (
        <ul className="mt-4">
          {/* Le filtre est ICI et pas seulement dans `LigneRequis` : `key=` est
              évalué par le parent, donc une entrée sans `requirement` lèverait
              avant même que l'enfant puisse se protéger. Mon premier essai avait
              la garde au mauvais niveau — le test l'a montré. */}
          {v.requirements
            .filter((etat) => etat?.requirement?.id)
            .map((etat) => (
              <LigneRequis
                key={etat.requirement.id}
                etat={etat}
                demandes={
                  etat.missing_information_id !== null
                    ? (parRequis.get(etat.missing_information_id) ?? [])
                    : []
                }
                onDemander={setDemande}
              />
            ))}
        </ul>
      )}

      {ajoutOuvert && (
        <AddRequirementDialog projectId={projectId} onClose={() => setAjoutOuvert(false)} />
      )}

      {demande !== null && (
        <AskClientDialog
          projectId={projectId}
          companyId={companyId}
          requirement={{
            id: demande.requirement.id,
            kind: demande.requirement.kind,
            requirement_key: demande.requirement.requirement_key,
            label: demande.requirement.label_fr,
          }}
          missingInformationId={demande.missing_information_id}
          onClose={() => setDemande(null)}
        />
      )}
    </section>
  );
}
